import _ from 'lodash';
import { getCKEditorInstance } from '@/annotation/ckEditor.utilities';
import { sqReportStore, sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { SeeqNames } from '@seeqdev/constants';
import type { Position } from 'ckeditor5';
import type { CkEditorInstanceId } from '@/annotation/ckEditor.utilities';

/**
 * Gets the data from CKEditor
 */
export function getHtmlReportEditor() {
  const ckeditorInstance = getCKEditorInstance('reportEditor');
  const isDashboard = !!sqWorkbookStore.getWorksheet(sqWorkbenchStore.stateParams.worksheetId)?.isDashboard;
  if (isDashboard) {
    // TODO: CRAB-39312
    // it's a dashboard, so we need to pass back the content IDs so they don't get archived. This matches the
    // regex that the backend uses to parse the content. We don't want to include the raw HTML as it can contain
    // images which belong to the individual text content cells which are stored as annotations in the backend. We
    // don't allow the same image URL to be used across two annotations, so including them would break this save.
    return `<div>${sqReportStore.contentNotArchived
      .map((content) => `${SeeqNames.TopicDocumentAttributes.DataSeeqContent}="${content.id}"`)
      .join(',')}</div>`;
  }

  if (!ckeditorInstance) {
    throw new Error('CKEditor instance not found for report editor');
  }

  return ckeditorInstance.getData();
}

/**
 * Gets the editor scroll offset
 *
 * @returns the current scroll offset
 */
export function getScrollOffset() {
  const editorMain = document.querySelector('.ck-editor__main');
  return editorMain?.scrollTop ?? 0;
}

/**
 * Sets the editor scroll offset
 *
 * @param offset - a scroll offset
 */
export function setScrollOffset(offset: number) {
  // Setting the scroll offset right after a change to the document can sometimes cause the set to be eaten by
  // something CK does.
  setTimeout(() => {
    const editorMain = document.querySelector('.ck-editor__main');
    if (editorMain) {
      editorMain.scrollTop = offset;
    }
  }, 1);
}

export function setCursorPosition(cursorPosition: Position | null | undefined, id: CkEditorInstanceId): void {
  if (_.isNil(cursorPosition)) {
    return;
  }

  getCKEditorInstance(id)?.model.change((writer) => {
    try {
      writer.setSelection(cursorPosition);
    } catch (error) {
      // It is expected that the path can be incorrect, such as during concurrent editing and the text where another
      // had their cursor is removed. It is easier to ignore the error than try to devise our own method for figuring
      // out if it is correct. However, a correct cursor position still needs to be set to avoid CK erroring when the
      // user starts typing again.
      (cursorPosition.path as any) = [0, 0];
      writer.setSelection(cursorPosition);
    }
  });
}

export function getCursorPosition(id: CkEditorInstanceId): undefined | Position {
  return getCKEditorInstance(id)?.model.document.selection.getFirstPosition() ?? undefined;
}

export function executeCommand<T extends { command: string; ckEditorInstanceId: CkEditorInstanceId }>({
  command,
  ckEditorInstanceId,
  ...rest
}: T): void {
  getCKEditorInstance(ckEditorInstanceId)?.execute(command, rest);
}
